import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Music2, Users, User, Calendar, ArrowRight, MapPin, MapPinOff, Info } from 'lucide-react';
import { Swiper } from '../components/ui/Swiper';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import ParticleCanvas from '../components/ui/ParticleCanvas';
import { Headline } from '../components/ui/Headline';
import { BandPanel } from '../components/BandPanel';
import { MusicianPanel } from '../components/MusicianPanel';
import { StoryPanel } from '../components/StoryPanel';
import { EventPanel } from '../components/EventPanel';
import { StoryModal } from '../components/StoryModal';
import { StoryLikeButton } from '../components/StoryLikeButton';
import { SEOHead } from '../components/SEOHead';
import exploreBandsImage from '../assets/explore-bands.avif';
import startJourneyImage from '../assets/start-journey.avif';
import type { Event, Profile, Band, Story } from '../types/database';

type BandWithMembers = Band & {
  members: Profile[];
};

type StoryWithDetails = Story & {
  author: Profile;
  band: Band;
  likes_count: number;
};

type EventWithDetails = Event & {
  band: Band;
};

function Home() {
  const { t } = useTranslation();
  const [featuredBands, setFeaturedBands] = useState<BandWithMembers[]>([]);
  const [newMusicians, setNewMusicians] = useState<Profile[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<EventWithDetails[]>([]);
  const [latestStories, setLatestStories] = useState<StoryWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState<StoryWithDetails | null>(null);

  const handleStorySelect = (story: StoryWithDetails) => {
    setSelectedStory(story);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch featured bands with their members
        const { data: bandsData } = await supabase
          .from('bands')
          .select(`
            *,
            members:band_members(profile:profiles(*))
          `)
          .limit(5);

        setFeaturedBands(bandsData as BandWithMembers[] || []);

        // Fetch new musicians
        const { data: musiciansData } = await supabase
          .from('profiles')
          .select('*')
          .eq('is_musician', true)
          .order('created_at', { ascending: false })
          .limit(5);

        setNewMusicians(musiciansData || []);

        // Fetch upcoming events
        const { data: eventsData } = await supabase
          .from('events')
          .select('*')
          .gte('event_date', new Date().toISOString())
          .order('event_date', { ascending: true })
          .limit(5);

        const eventsWithBands = await Promise.all(
          (eventsData || []).map(async (event) => {
            const { data: eventBands } = await supabase
              .from('event_bands')
              .select('band:bands(*)')
              .eq('event_id', event.id)
              .limit(1)
              .maybeSingle();

            return {
              ...event,
              band: eventBands?.band || null,
            };
          })
        );

        setUpcomingEvents(eventsWithBands as EventWithDetails[]);

        // Fetch latest stories with explicit relationship specifications
        const { data: storiesData } = await supabase
          .from('stories')
          .select(`
            *,
            author:profiles!stories_author_id_fkey(*),
            band:bands!stories_band_id_fkey(*)
          `)
          .order('created_at', { ascending: false })
          .limit(5);

        // Get likes count for each story
        const storiesWithLikes = await Promise.all(
          (storiesData || []).map(async (story) => {
            const { count: likesCount } = await supabase
              .from('story_likes')
              .select('*', { count: 'exact' })
              .eq('story_id', story.id);

            return {
              ...story,
              likes_count: likesCount || 0,
            };
          })
        );

        setLatestStories(storiesWithLikes);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "jamnation",
    "description": "Die Musikszene in Deinen Händen - Entdecke Bands, Musiker:innen und Events in der Schweiz",
    "url": "https://jamnation.ch",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://jamnation.ch/bands?search={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://www.facebook.com/jamnation.ch",
      "https://www.instagram.com/jamnation.ch"
    ]
  };

  return (
    <>
      <SEOHead
        title="jamnation - Die Musikszene in Deinen Händen"
        description="Entdecke die Schweizer Musikszene auf jamnation. Finde Bands, vernetze dich mit Musikern, entdecke Events und teile deine musikalische Reise. Die Plattform für Musiker:innen, Bands und Musikbegeisterte in der Schweiz."
        keywords={['Musikszene Schweiz', 'Bands Schweiz', 'Musiker vernetzen', 'Musik Events', 'Schweizer Bands', 'Musikplattform', 'Band finden', 'Musiker finden']}
        url="https://jamnation.ch"
        structuredData={structuredData}
      />

      <section className="gradient-bg relative">
        <ParticleCanvas className="absolute bottom-0 left-0 top-0 right-0" />
        <div className="container mx-auto px-6 my-8">
          <div className="max-w-6xl flex flex-col justify-center">
            <h1 
              className="font-tilt-warp text-4xl md:text-5xl lg:text-7xl leading-extra-tight light mb-12"
              dangerouslySetInnerHTML={{ __html: t('home.hero.title') }}
            />
            <p className="text-xl md:text-2xl leading-relaxed mb-12">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex gap-4">
              <Link to="/register" className="btn bg-dark hover:bg-dark/80 text-lg px-8 py-4 inline-flex items-center group w-fit">
                {t('home.hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5 transform transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/faq" className="btn hover:bg-dark/80 text-lg flex items-center">
                <Info className="w-5 h-5 mr-2" />
                FAQ
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 pb-8 pt-24 md:py-24">
        <div className="space-y-24">
          {/* Featured Bands */}
          <section>
            <Headline title={t('home.sections.featuredBands')} size={3} viewAllLink="bands" />
            <Swiper>
              {featuredBands.map((band) => (
                <BandPanel key={band.id} band={band} />
              ))}
            </Swiper>
          </section>

          {/* Teaser: Find Your Band - Content Left, Image Right */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-0 items-stretch bg-gray-custom/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
              {/* Content Section - 2/3 width */}
              <div className="md:col-span-2 p-6 md:p-12 flex flex-col justify-center order-2 md:order-1">
                <h2 className="font-tilt-warp text-3xl md:text-5xl text-light mb-6">
                  {t('home.bandTeaser.title')}
                </h2>
                <p className="text-light/60 text-lg mb-8 leading-relaxed">
                  {t('home.bandTeaser.content')}
                </p>
                <Link to="/bands" className="btn-secondary w-fit">
                  {t('home.bandTeaser.cta')}
                </Link>
              </div>
              
              {/* Image Section - 1/3 width */}
              <div className="md:col-span-1 relative h-80 md:h-96 order-1 md:order-2">
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${exploreBandsImage})`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-primary/10 to-primary/40" />
                {/* Fade effect from right to left */}
                <div className="absolute inset-0 bg-gradient-to-l from-transparent to-gray-custom/10" />
              </div>
            </div>
          </div>

          {/* Upcoming Events */}
          <section>
            <Headline title={t('home.sections.upcomingEvents')} size={3} viewAllLink="agenda" />
            <Swiper>
              {upcomingEvents.map((event) => (
                <EventPanel key={event.id} event={event} />
              ))}
            </Swiper>
          </section>

          {/* Latest Stories */}
          <section>
            <Headline title={t('home.sections.latestStories')} size={3} viewAllLink="stories" />
            <Swiper>
              {latestStories.map((story) => (
                <StoryPanel key={story.id} onClick={handleStorySelect} story={story} />
              ))}
            </Swiper>
          </section>

          {/* Teaser: Share Your Journey - Image Left, Content Right */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-0 items-stretch bg-gray-custom/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
              {/* Image Section - 1/3 width */}
              <div className="md:col-span-1 relative h-80 md:h-96">
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${startJourneyImage})`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary/10 to-secondary/40" />
                {/* Fade effect from left to right */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-custom/10" />
              </div>
              
              {/* Content Section - 2/3 width */}
              <div className="md:col-span-2 p-6 md:p-12 flex flex-col justify-center">
                <h2 className="font-tilt-warp text-3xl md:text-5xl text-light mb-6">
                  {t('home.musicianTeaser.title')}
                </h2>
                <p className="text-light/60 text-lg mb-8 leading-relaxed">
                  {t('home.musicianTeaser.content')}
                </p>
                <Link to="/register" className="btn-secondary w-fit">
                  {t('home.musicianTeaser.cta')}
                </Link>
              </div>
            </div>
          </div>

          {/* New Musicians */}
          <section>
            <Headline title={t('home.sections.newMusicians')} size={3} viewAllLink="musicians" />
            <Swiper>
              {newMusicians.map((musician) => (
                <MusicianPanel key={musician.id} musician={musician} />
              ))}
            </Swiper>
          </section>
        </div>
      </div>

      {selectedStory && (
        <StoryModal
          story={selectedStory}
          isOpen={true}
          onClose={() => setSelectedStory(null)}
        />
      )}
    </>
  );
}

export default Home;